import { INewFolder } from '@commenda/types/src/drive-view/types';

export interface FolderPathInfo {
  path: string; // e.g. "Bank statements > FY24"
  folderIds: string[]; // ['abc123', 'def456']
  folderNames: string[]; // ['Bank statements', 'FY24']
}

export class DriveViewHelper {
  /**
   * Depth-first search for `fileId` in the provided folder tree.
   *
   * @param rootWrapper Object shaped `{ folder: INewFolder }`
   * @param fileId      `IDocument.id` you’re looking for
   *
   * @returns `{ path, folderIds }` or `null` when the file isn’t found
   */
  static getFilePathInfo(
    { folder: root }: { folder: INewFolder },
    fileId: number
  ): FolderPathInfo | null {
    type Trail = { name: string; id: string }[];

    /** DFS helper – returns the trail if the file is under `current`. */
    const dfs = (current: INewFolder, trail: Trail): Trail | null => {
      // 1️⃣  File directly inside this folder?
      if (current.files.some((d) => d.id === fileId)) {
        return [...trail, { name: current.name, id: current.id }];
      }

      // 2️⃣  Search children
      for (const sub of current.folders) {
        const found = dfs(sub, [
          ...trail,
          { name: current.name, id: current.id },
        ]);
        if (found) return found;
      }
      return null;
    };

    const trail = dfs(root, []);
    if (!trail) return null; // file not found

    const folderNames = trail.map((t) => t.name);
    const folderIds = trail.map((t) => t.id);

    return {
      path: folderNames.join(' > '),
      folderIds,
      folderNames,
    };
  }
}
